import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { StoreDataTypeKey } from '@etoh/database/core';
import { init } from 'commandbar';
import { distinctUntilChanged, firstValueFrom, take } from 'rxjs';
import { StoreService } from '../features/store.service';
import { UserService } from '../user.service';

import { take as firsts, toNumber, uniq } from 'lodash';

import uFuzzy from '@leeoniya/ufuzzy';

import { ICommandFromClientType } from 'commandbar/build/internal/src/middleware/ICommandFromClientType';
import { getAddressDisplay } from '../features/addresses/addresses.pure';
import { ContactsService } from '../features/contacts/contacts.service';
import { getEntitiesWording } from '../features/entities/entities.wordings';
import { getItemsTooltip } from '../features/items/items.pure';

const dataToSearchs: StoreDataTypeKey[] = [
  'contacts',
  'items',
  'companies',
  'invoices',
  'addresses',
  'buySpecs',
  'sampleShipmentRequests',
  'sampleShipments',
  'agreements',
  'shipmentFulfillments',
];

init('1afecf05');

interface SearchEntry {
  id: number | string;
  dataType: StoreDataTypeKey;
  labelvalue: string;
  descriptionvalue: string;
  editUrl: string;
  searchText: string;
}

@Injectable({
  providedIn: 'root',
})
export class CommandbarService implements OnDestroy {
  private readonly uf = new uFuzzy({});
  private readonly indexByType = new Map<StoreDataTypeKey, SearchEntry[]>();
  private readonly indexedVersions = new Map<StoreDataTypeKey, number>();

  private flatIndex: SearchEntry[] = [];
  private indexRefreshPromise: Promise<void> | null = null;
  private indexInitializedForUserId: string | null = null;
  private currentUserId: string | null = null;
  private routerRegistered = false;
  private recordsRegistered = false;
  private eventSubscriberRegistered = false;
  private removeEventSubscriber: (() => void) | null = null;

  constructor(
    private userService: UserService,
    private router: Router,
    private storeService: StoreService,
    private contactsService: ContactsService
  ) {
    this.userService.user$
      .pipe(
        distinctUntilChanged(
          (previous, current) => previous?.id === current?.id
        )
      )
      .subscribe((user) => {
        if (!user) {
          this.currentUserId = null;
          this.clearSearchIndex();
          window.CommandBar.shutdown();
          return;
        }

        this.currentUserId = user.id;
        window.CommandBar.boot(user.id);

        if (!this.routerRegistered) {
          this.setRouter();
          this.routerRegistered = true;
        }

        if (!this.eventSubscriberRegistered) {
          this.registerCommandbarEvents();
          this.eventSubscriberRegistered = true;
        }

        if (!this.recordsRegistered) {
          this.loadDatas();
          this.recordsRegistered = true;
        }

        if (
          this.indexInitializedForUserId &&
          this.indexInitializedForUserId !== user.id
        ) {
          this.clearSearchIndex();
        }
      });
  }

  ngOnDestroy(): void {
    this.removeEventSubscriber?.();
    this.clearSearchIndex();
  }

  private setRouter() {
    const routerFunc = (newUrl: string) => this.router.navigateByUrl(newUrl);
    window.CommandBar.addRouter(routerFunc);
  }

  private loadDatas() {
    window.CommandBar.addRecords('entitiesId', [], {
      onInputChange: async (input) => {
        const numberInput = toNumber(input);

        if (isNaN(numberInput)) {
          return [];
        }

        const datas = await this.getSearchIndex();
        const x = datas.filter((d) => d.id === numberInput);

        return x;
      },
      labelKey: 'labelvalue',
      descriptionKey: 'descriptionvalue',
    });

    window.CommandBar.addRecords('entities', [], {
      onInputChange: async (input) => {
        if (input?.length < 3) {
          return [];
        }

        const datas = await this.getSearchIndex();
        const onlyStringValues = datas.map((entry) => entry.searchText);

        const idxs = this.uf.filter(onlyStringValues, input);

        if (idxs) {
          const info = this.uf.info(idxs, onlyStringValues, input);
          const order = this.uf.sort(info, onlyStringValues, input);

          const firstsEntitiesId = firsts(order, 10);

          return firstsEntitiesId.map((id) => {
            const extendedEntity = datas[idxs[id]];

            const infoIdx = order[id];

            const highlight = uFuzzy.highlight(
              onlyStringValues[info.idx[infoIdx]],
              info.ranges[infoIdx]
            );

            const shortedHighlight = highlight.match(
              /.{15}<mark>.*<\/mark>.{15}/gm
            )?.[0];

            return {
              ...extendedEntity,
              descriptionvalue: `${extendedEntity.descriptionvalue} ${shortedHighlight}`,
            };
          });
        }

        return [];
      },
      labelKey: 'labelvalue',
      descriptionKey: 'descriptionvalue',
    });

    const getAction = (action: string): ICommandFromClientType => {
      return {
        text: `Open {{record.dataType}}`,
        name: `open_{{record.dataType}}_${action}`,
        heading: `{{record.dataType}}`,
        explanation: `Yolo`,
        category: '{{record.dataType}}',
        template: {
          type: 'link',
          value: `{{record.editUrl}}/{{record.id}}`,
          operation: 'router',
        },
      };
    };

    window.CommandBar.addRecordAction('entitiesId', getAction('itemsId'));
    window.CommandBar.addRecordAction('entities', getAction('entities'));
  }

  private async getSearchIndex(): Promise<SearchEntry[]> {
    await this.refreshSearchIndexIfNeeded();
    return this.flatIndex;
  }

  public preloadSearchIndex(): Promise<void> {
    return this.refreshSearchIndexIfNeeded();
  }

  private registerCommandbarEvents(): void {
    void window.CommandBar.addEventSubscriber((eventName) => {
      if (eventName === 'opened') {
        void this.preloadSearchIndex();
      }
    }).then((removeSubscriber) => {
      this.removeEventSubscriber = removeSubscriber;
    });
  }

  private async refreshSearchIndexIfNeeded(): Promise<void> {
    if (!this.currentUserId) {
      this.flatIndex = [];
      return;
    }

    if (this.indexRefreshPromise) {
      return this.indexRefreshPromise;
    }

    const refreshUserId = this.currentUserId;
    const refreshPromise = (async () => {
      const currentVersions = await this.getCurrentVersions();

      if (this.currentUserId !== refreshUserId) {
        return;
      }

      const dirtyTypes = this.getDirtyTypes(currentVersions, refreshUserId);

      if (!dirtyTypes.length && this.indexInitializedForUserId === refreshUserId) {
        return;
      }

      const rebuiltSlices = await Promise.all(
        dirtyTypes.map(async (dataType) => {
          const datas = await firstValueFrom(
            this.storeService.store[dataType].data$.pipe(take(1))
          );

          const version = await firstValueFrom(
            this.storeService.store[dataType].version$.pipe(take(1))
          );

          return {
            dataType,
            datas,
            version,
          };
        })
      );

      if (this.currentUserId !== refreshUserId) {
        return;
      }

      rebuiltSlices.forEach(({ dataType, datas, version }) => {
        this.indexByType.set(
          dataType,
          datas.map((entity) => this.createSearchEntry(entity, dataType))
        );
        this.indexedVersions.set(dataType, version);
      });

      this.flatIndex = dataToSearchs.flatMap(
        (dataType) => this.indexByType.get(dataType) ?? []
      );
      this.indexInitializedForUserId = refreshUserId;
    })().finally(() => {
      if (this.indexRefreshPromise === refreshPromise) {
        this.indexRefreshPromise = null;
      }
    });

    this.indexRefreshPromise = refreshPromise;
    return refreshPromise;
  }

  private async getCurrentVersions(): Promise<
    Map<StoreDataTypeKey, number>
  > {
    const versions = await Promise.all(
      dataToSearchs.map(async (dataType) => {
        const version = await firstValueFrom(
          this.storeService.store[dataType].version$.pipe(take(1))
        );

        return [dataType, version] as const;
      })
    );

    return new Map(versions);
  }

  private getDirtyTypes(
    currentVersions: Map<StoreDataTypeKey, number>,
    userId: string
  ): StoreDataTypeKey[] {
    if (this.indexInitializedForUserId !== userId) {
      return [...dataToSearchs];
    }

    return dataToSearchs.filter((dataType) => {
      return this.indexedVersions.get(dataType) !== currentVersions.get(dataType);
    });
  }

  private clearSearchIndex(): void {
    this.indexByType.clear();
    this.indexedVersions.clear();
    this.flatIndex = [];
    this.indexRefreshPromise = null;
    this.indexInitializedForUserId = null;
  }

  private createSearchEntry(
    entity: any,
    dataType: StoreDataTypeKey
  ): SearchEntry {
    const extendedEntity = this.extendEntity(entity, dataType);

    return {
      ...extendedEntity,
      searchText: uniq([
        ...this.collectSearchTokens(entity),
        extendedEntity.labelvalue,
        extendedEntity.descriptionvalue,
      ])
        .filter((token) => !!token)
        .join(' '),
    };
  }

  private collectSearchTokens(value: unknown, depth = 0): string[] {
    if (value === null || value === undefined) {
      return [];
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return [String(value)];
    }

    if (typeof value === 'boolean') {
      return value ? ['true'] : ['false'];
    }

    if (depth >= 2) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.flatMap((entry) =>
        this.collectSearchTokens(entry, depth + 1)
      );
    }

    if (typeof value === 'object') {
      return Object.values(value as Record<string, unknown>).flatMap((entry) =>
        this.collectSearchTokens(entry, depth + 1)
      );
    }

    return [];
  }

  private extendEntity(entity: any, dataType: StoreDataTypeKey) {
    const labelvalue = `${getEntitiesWording(dataType)}: ${entity.id}`;
    let descriptionvalue: string = dataType;

    if (dataType === 'contacts') {
      descriptionvalue = `${this.contactsService.getLabelForContact(entity)}`;
    } else if (dataType === 'items') {
      descriptionvalue = `${getItemsTooltip(entity)}`;
    } else if (dataType === 'companies') {
      descriptionvalue = `${entity.name}`;
    } else if (dataType === 'invoices') {
      descriptionvalue = `${entity.id}`;
    } else if (dataType === 'addresses') {
      descriptionvalue = `${getAddressDisplay(entity)}`;
    } else if (dataType === 'buySpecs') {
      descriptionvalue = `${entity.id}`;
    } else if (dataType === 'sampleShipmentRequests') {
      descriptionvalue = `${entity.id}`;
    } else if (dataType === 'sampleShipments') {
      descriptionvalue = `${entity.id}`;
    } else if (dataType === 'agreements') {
      descriptionvalue = `${entity.id}`;
    } else if (dataType === 'shipmentFulfillments') {
      descriptionvalue = `${entity.id}`;
    }

    return {
      id: entity.id,
      dataType,
      labelvalue,
      descriptionvalue: `${descriptionvalue}` ?? '',
      editUrl: `/features/${dataType}/edit`,
    };
  }
}
